import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import styles from './VacanciesPage.module.css';
import { applyToVacancy, getPublicVacancies } from '../../api/teamVacancies';
import { useAuth } from '../../context/AuthContext';
import { listWantTeamUsers } from '../../api/search';
import { getMyTeams, sendTeamInvite } from '../../api/teams';
import { buildDisplayName } from '../../utils/userDisplay';
import { FINAL_RESULT_TRACKS } from '../../utils/projectTracks';

const PAGE_SIZE = 20;
const TRACK_DISPLAY_NAMES = {
  'Партнеры': 'ЮФУ.Про Партнеров',
  'Стартапы': 'ЮФУ.Про Стартапы',
  'Исследования': 'ЮФУ.Про Исследования',
  'Общество': 'ЮФУ.Про Общество',
  'Организация': 'ЮФУ.Про Организацию',
};
const TRACK_TABS = [
  { value: 'ALL', label: 'Все' },
  ...FINAL_RESULT_TRACKS.map(value => ({ value, label: value })),
  { value: 'ЮФУ.Про Партнеров', label: 'ЮФУ.Про Партнеров' },
  { value: 'ЮФУ.Про Стартапы', label: 'ЮФУ.Про Стартапы' },
  { value: 'ЮФУ.Про Исследования', label: 'ЮФУ.Про Исследования' },
  { value: 'ЮФУ.Про Общество', label: 'ЮФУ.Про Общество' },
  { value: 'ЮФУ.Про Организацию', label: 'ЮФУ.Про Организацию' },
];

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timerId = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timerId);
  }, [value, delayMs]);

  return debounced;
}

function getTrackDisplayName(value) {
  const normalized = String(value || '').trim();
  return TRACK_DISPLAY_NAMES[normalized] || normalized;
}

export function VacanciesPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vacancies');
  const [query, setQuery] = useState('');
  const [vacancies, setVacancies] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [activeTrack, setActiveTrack] = useState('ALL');

  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidateResults, setCandidateResults] = useState([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateError, setCandidateError] = useState('');
  const [candidateOffset, setCandidateOffset] = useState(0);
  const [candidateHasMore, setCandidateHasMore] = useState(false);
  const [inviteMessageByUser, setInviteMessageByUser] = useState({});
  const [inviteLoadingId, setInviteLoadingId] = useState(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');

  const debouncedQuery = useDebouncedValue(query);
  const debouncedCandidateQuery = useDebouncedValue(candidateQuery);

  const loadVacancies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPublicVacancies({
        query: debouncedQuery,
        track_label: activeTrack === 'ALL' ? undefined : activeTrack,
        limit: PAGE_SIZE,
        offset
      });
      setVacancies(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number.isFinite(data?.total) ? data.total : 0);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить вакансии.');
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, offset, activeTrack]);

  useEffect(() => {
    loadVacancies();
  }, [loadVacancies]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTeams([]);
      setSelectedTeamId('');
      return;
    }
    let isCancelled = false;
    getMyTeams()
      .then((data) => {
        if (isCancelled) return;
        const captainTeams = (Array.isArray(data) ? data : []).filter(
          (team) => team?.system_role === 'CAPTAIN'
        );
        setTeams(captainTeams);
        if (captainTeams.length && !selectedTeamId) {
          setSelectedTeamId(String(captainTeams[0].team_id));
        }
      })
      .catch(() => {
        if (!isCancelled) setTeams([]);
      });
    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, selectedTeamId]);

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'candidates') return;
    const queryValue = debouncedCandidateQuery.trim();
    let isCancelled = false;
    setCandidateLoading(true);
    setCandidateError('');
    listWantTeamUsers({ query: queryValue, limit: PAGE_SIZE, offset: candidateOffset })
      .then((response) => {
        if (isCancelled) return;
        setCandidateResults(Array.isArray(response?.items) ? response.items : []);
        setCandidateHasMore(Boolean(response?.has_more));
      })
      .catch((err) => {
        if (isCancelled) return;
        setCandidateResults([]);
        setCandidateError(err.message || 'Не удалось загрузить кандидатов.');
        setCandidateHasMore(false);
      })
      .finally(() => {
        if (!isCancelled) setCandidateLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [debouncedCandidateQuery, isAuthenticated, activeTab, candidateOffset]);

  const handleApply = async (vacancyId) => {
    if (!isAuthenticated) return;
    setActionLoadingId(vacancyId);
    setActionMessage('');
    setError('');
    try {
      await applyToVacancy(vacancyId);
      setActionMessage('Отклик отправлен.');
    } catch (err) {
      setError(err.message || 'Не удалось отправить отклик.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleInvite = async (userId) => {
    if (!selectedTeamId) {
      setInviteMessage('Сначала выберите команду.');
      return;
    }
    const message = String(inviteMessageByUser[userId] || '').trim();
    if (!message) {
      setInviteMessage('Добавьте текст приглашения.');
      return;
    }
    setInviteMessage('');
    setInviteLoadingId(userId);
    try {
      await sendTeamInvite(selectedTeamId, userId, message);
      setInviteMessageByUser((prev) => ({ ...prev, [userId]: '' }));
      setInviteMessage('Приглашение отправлено.');
    } catch (err) {
      setInviteMessage(err.message || 'Не удалось отправить приглашение.');
    } finally {
      setInviteLoadingId(null);
    }
  };

  const groupedVacancies = useMemo(() => {
    const grouped = new Map();
    (vacancies || []).forEach((vacancy) => {
      const key = vacancy.team_id;
      if (!grouped.has(key)) {
        grouped.set(key, {
          team_id: vacancy.team_id,
          team_name: vacancy.team_name,
          track_label: vacancy.track_label,
          items: []
        });
      }
      grouped.get(key).items.push(vacancy);
    });
    return Array.from(grouped.values());
  }, [vacancies]);

  const canInvite = Boolean(isAuthenticated && teams.length && selectedTeamId);
  const candidateEmptyMessage = debouncedCandidateQuery.trim().length
    ? 'Кандидатов по запросу не найдено.'
    : 'Пока нет участников с отметкой «Хочу в команду».';

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/profile');
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backButton} onClick={handleBack}>
        Назад
      </button>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Поиск команд и кандидатов</h1>
          <p className={styles.subtitle}>
            Команды ищут участников, а кандидаты — места в командах. Выберите нужный сценарий и действуйте.
          </p>
        </div>
        <div className={styles.tabRow}>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'vacancies' ? styles.tabBtnActive : ''}`}
            onClick={() => setActiveTab('vacancies')}
          >
            Ищут людей
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'candidates' ? styles.tabBtnActive : ''}`}
            onClick={() => setActiveTab('candidates')}
          >
            Хотят в команду
          </button>
        </div>
      </div>

      {activeTab === 'vacancies' ? (
        <>
          <div className={styles.controls}>
            <input
              className={styles.search}
              type="text"
              placeholder="Поиск по названию, описанию или команде"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div className={styles.trackTabs}>
            {TRACK_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`${styles.trackTab} ${activeTrack === tab.value ? styles.trackTabActive : ''}`}
                onClick={() => {
                  setActiveTrack(tab.value);
                  setOffset(0);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {actionMessage ? <p className={styles.success}>{actionMessage}</p> : null}
          {error ? <p className={styles.error}>{error}</p> : null}
          {loading ? <p className={styles.note}>Загружаем вакансии...</p> : null}

          {!loading && !groupedVacancies.length ? <p className={styles.note}>Вакансии пока не опубликованы.</p> : null}

          <div className={styles.list}>
            {groupedVacancies.map((group) => (
              <article key={group.team_id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <div>
                    <h3 className={styles.cardTitle}>{group.team_name}</h3>
                    {group.track_label ? (
                      <span className={styles.trackLabel}>{getTrackDisplayName(group.track_label)}</span>
                    ) : null}
                  </div>
                  <span className={styles.teamMeta}>Вакансий: {group.items.length}</span>
                </div>
                <div className={styles.vacancyList}>
                  {group.items.map((vacancy) => (
                    <div key={vacancy.vacancy_id} className={styles.vacancyRow}>
                      <div>
                        <p className={styles.vacancyTitle}>{vacancy.title}</p>
                        <p className={styles.vacancyDescription}>{vacancy.description}</p>
                        <span className={styles.vacancyMeta}>Откликов: {vacancy.applicants_count}</span>
                      </div>
                      <div className={styles.cardActions}>
                        {isAuthenticated ? (
                          <button
                            className={styles.primaryBtn}
                            type="button"
                            onClick={() => handleApply(vacancy.vacancy_id)}
                            disabled={actionLoadingId === vacancy.vacancy_id}
                          >
                            Откликнуться
                          </button>
                        ) : (
                          <Link className={styles.primaryBtn} to="/login">
                            Войти чтобы откликнуться
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          {total > PAGE_SIZE ? (
            <div className={styles.pagination}>
              <button
                className={styles.secondaryBtn}
                type="button"
                onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                disabled={offset === 0}
              >
                Назад
              </button>
              <button
                className={styles.secondaryBtn}
                type="button"
                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
              >
                Дальше
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <section className={styles.candidates}>
          {!isAuthenticated ? (
            <div className={styles.noteBox}>
              <p>Войдите, чтобы искать участников и отправлять приглашения.</p>
              <Link className={styles.primaryBtn} to="/login">Войти в кабинет</Link>
            </div>
          ) : (
            <>
              <div className={styles.candidateControls}>
                <input
                  className={styles.search}
                  type="text"
                  placeholder="Поиск по ФИО, логину или навыку"
                  value={candidateQuery}
                  onChange={(e) => {
                    setCandidateQuery(e.target.value);
                    setCandidateOffset(0);
                  }}
                />
                <select
                  className={styles.teamSelect}
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                >
                  {teams.length ? (
                    teams.map((team) => (
                      <option key={team.team_id} value={team.team_id}>
                        {team.name}
                      </option>
                    ))
                  ) : (
                    <option value="">Нет команд капитана</option>
                  )}
                </select>
              </div>

              {teams.length === 0 ? (
                <p className={styles.note}>Приглашения могут отправлять только капитаны команд.</p>
              ) : null}
              {inviteMessage ? <p className={styles.success}>{inviteMessage}</p> : null}
              {candidateError ? <p className={styles.error}>{candidateError}</p> : null}
              {candidateLoading ? <p className={styles.note}>Загружаем кандидатов...</p> : null}

              {!candidateLoading && !candidateResults.length ? (
                <p className={styles.note}>{candidateEmptyMessage}</p>
              ) : null}

              <div className={styles.candidateList}>
                {candidateResults.map((candidate) => {
                  const displayName = buildDisplayName(candidate) || `@${candidate.login}`;
                  const shortBio = String(candidate.bio || '').trim();
                  const shortText = shortBio.length > 180 ? `${shortBio.slice(0, 180)}…` : shortBio;
                  return (
                    <article key={candidate.user_id} className={styles.candidateCard}>
                      <div className={styles.candidateHeader}>
                        <div>
                          <strong>{displayName}</strong>
                          <span className={styles.candidateLogin}>@{candidate.login}</span>
                        </div>
                        <Link className={styles.profileLink} to={`/search/users/${candidate.user_id}`}>
                          Профиль
                        </Link>
                      </div>
                      {candidate.want_team ? (
                        <span className={styles.wantTeamBadge}>Хочу в команду</span>
                      ) : null}
                      <p className={styles.candidateBio}>
                        {shortText || 'Описание пока не заполнено.'}
                      </p>
                      <div className={styles.skills}>
                        {(candidate.skills || []).slice(0, 6).map((skill) => (
                          <span key={`${candidate.user_id}-${skill}`} className={styles.skillChip}>
                            {skill}
                          </span>
                        ))}
                      </div>
                      <div className={styles.inviteBox}>
                        <textarea
                          className={styles.inviteInput}
                          placeholder="Текст приглашения"
                          rows={2}
                          value={inviteMessageByUser[candidate.user_id] || ''}
                          onChange={(e) =>
                            setInviteMessageByUser((prev) => ({
                              ...prev,
                              [candidate.user_id]: e.target.value
                            }))
                          }
                        />
                        <button
                          className={styles.primaryBtn}
                          type="button"
                          disabled={!canInvite || inviteLoadingId === candidate.user_id}
                          onClick={() => handleInvite(candidate.user_id)}
                        >
                          Отправить приглашение
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
              {candidateHasMore || candidateOffset > 0 ? (
                <div className={styles.pagination}>
                  <button
                    className={styles.secondaryBtn}
                    type="button"
                    onClick={() => setCandidateOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                    disabled={candidateOffset === 0}
                  >
                    Назад
                  </button>
                  <button
                    className={styles.secondaryBtn}
                    type="button"
                    onClick={() => setCandidateOffset((prev) => prev + PAGE_SIZE)}
                    disabled={!candidateHasMore}
                  >
                    Дальше
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>
      )}
    </div>
  );
}
